// src/contexts/TermsContext.tsx
// Gate de aceite dos TERMOS DE USO (issue #6). Segue o padrão de singleton
// bridge de DialogContext/BusyContext (ADR-004): o interceptor do axios (fora
// da árvore React) chama `requestTermsAcceptance` para abrir o modal de aceite
// e aguardar a decisão do usuário, sem re-login.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTheme } from './ThemeContext';
import { apiGet, apiPut } from '@/services/api';
import TermsModal from '@/components/common/TermsModal';
import type { TermosVigente } from '@/types/termos';
import { logger } from '@/utils/logger';

export type TermsMode = 'accept' | 'view';

interface TermsContextValue {
  // Habilita o item "Termos de Uso" no dropdown — só após o aceite vigente.
  hasAccepted: boolean;
  // Abre o modal em modo aceite e resolve true (aceitou) / false (recusou).
  openAcceptance: (versaoAtual: string) => Promise<boolean>;
  // Abre o modal em modo visualização (só leitura).
  openView: () => void;
  // Marca que o usuário já aceitou a versão vigente.
  markAccepted: () => void;
}

const TermsContext = createContext<TermsContextValue | undefined>(undefined);

// Singleton bridge para callers fora do React (interceptor em api.ts). Mesmo
// padrão de `dialogApi` (DialogContext) e `busyApi` (BusyContext).
let termsApi: TermsContextValue | null = null;

// Single-flight: várias requests em 403 `termos_pendente` simultâneo compartilham
// UMA promise de aceite → um único modal; todas retomam após o aceite.
let inflightAcceptance: Promise<boolean> | null = null;

export const requestTermsAcceptance = (versaoAtual: string): Promise<boolean> => {
  if (inflightAcceptance) return inflightAcceptance;
  if (!termsApi) {
    logger.warn('requestTermsAcceptance chamado antes do TermsProvider montar; ignorado');
    return Promise.resolve(false);
  }
  inflightAcceptance = termsApi.openAcceptance(versaoAtual).finally(() => {
    inflightAcceptance = null;
  });
  return inflightAcceptance;
};

export const showTermsView = (): void => {
  if (!termsApi) {
    logger.warn('showTermsView chamado antes do TermsProvider montar; ignorado');
    return;
  }
  termsApi.openView();
};

// Marca aceite (habilita o item do dropdown). Chamado pelo interceptor em
// respostas protegidas 2xx (fora das rotas isentas do gate).
export const markTermsAccepted = (): void => {
  termsApi?.markAccepted();
};

export const TermsProvider = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<TermsMode>('accept');
  const [termos, setTermos] = useState<TermosVigente | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  // Promise pendente do aceite + versão-alvo recebida no 403 (versao_atual).
  const resolveRef = useRef<((accepted: boolean) => void) | null>(null);
  const versaoAlvoRef = useRef<string | null>(null);

  const fetchVigente = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const resp = await apiGet('/v1/pessoas/usuarios/termos-vigente');
      setTermos(resp.data as TermosVigente);
    } catch {
      setTermos(null);
      setErrorMsg('Não foi possível carregar os termos de uso. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  const openAcceptance = useCallback(
    (versaoAtual: string): Promise<boolean> => {
      versaoAlvoRef.current = versaoAtual || null;
      setMode('accept');
      setIsOpen(true);
      void fetchVigente();
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
      });
    },
    [fetchVigente]
  );

  const openView = useCallback(() => {
    setMode('view');
    setIsOpen(true);
    void fetchVigente();
  }, [fetchVigente]);

  const markAccepted = useCallback(() => {
    setHasAccepted(true);
  }, []);

  // Resolve a promise pendente (se houver) e fecha o modal.
  const settle = useCallback((accepted: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    versaoAlvoRef.current = null;
    setIsOpen(false);
    setSubmitting(false);
    if (resolve) resolve(accepted);
  }, []);

  const handleAccept = useCallback(async () => {
    // Versão a aceitar: a do 403 (`versao_atual`) ou, fallback, a de termos-vigente.
    const versao = versaoAlvoRef.current ?? termos?.versao;
    if (!versao) {
      setErrorMsg('Versão dos termos indisponível. Recarregue e tente novamente.');
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await apiPut('/v1/pessoas/usuarios/aceitar-termos', { versao });
      setHasAccepted(true);
      settle(true); // interceptor repete a request originalmente bloqueada
    } catch (err) {
      // 400 (versão desatualizada / sem vigente), 401, 500 → mantém modal aberto.
      const apiErr = err as { response?: { data?: { error?: string } } };
      setErrorMsg(
        apiErr.response?.data?.error ?? 'Não foi possível registrar o aceite. Tente novamente.'
      );
      setSubmitting(false);
    }
  }, [termos, settle]);

  const handleRefuse = useCallback(() => {
    // Recusar: mantém o usuário fora das rotas protegidas (resolve false → o
    // interceptor repropaga o 403 original).
    settle(false);
  }, [settle]);

  const handleCloseView = useCallback(() => {
    // Edge: se `openView` foi acionado com um aceite ainda pendente, resolve como
    // recusa para não vazar a promise (o single-flight travaria novos aceites).
    if (resolveRef.current) {
      settle(false);
      return;
    }
    setIsOpen(false);
  }, [settle]);

  const value = useMemo<TermsContextValue>(
    () => ({ hasAccepted, openAcceptance, openView, markAccepted }),
    [hasAccepted, openAcceptance, openView, markAccepted]
  );

  useEffect(() => {
    termsApi = value;
    return () => {
      if (termsApi === value) termsApi = null;
    };
  }, [value]);

  return (
    <TermsContext.Provider value={value}>
      {children}
      <TermsModal
        isOpen={isOpen}
        theme={theme}
        mode={mode}
        loading={loading}
        submitting={submitting}
        errorMsg={errorMsg}
        termos={termos}
        onAccept={handleAccept}
        onRefuse={handleRefuse}
        onCloseView={handleCloseView}
      />
    </TermsContext.Provider>
  );
};

export const useTerms = (): TermsContextValue => {
  const ctx = useContext(TermsContext);
  if (!ctx) throw new Error('useTerms deve ser usado dentro de um TermsProvider');
  return ctx;
};
